/**
 * Shared generic utilities for the frontend.
 */

export function getInitials(name?: string | null, email?: string | null): string {
  if (name) {
    const parts = name.split(" ").filter(Boolean);
    const first = parts[0];
    const second = parts[1];
    if (parts.length >= 2 && first && second && first.length > 0 && second.length > 0) {
      return (first.charAt(0) + second.charAt(0)).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return "??";
}

export function logoUrl(logo: string | null | undefined): string | null {
  if (!logo) return null;
  if (logo.startsWith("http://") || logo.startsWith("https://") || logo.startsWith("/")) return logo;
  return `/assets/${logo}`;
}

/** True when `value` is an absolute http(s) URL (http allowed for localhost dev). */
export function isAbsoluteHttpUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

export function formatDateTime(dateStr: string | Date | null | undefined): string {
  if (!dateStr) return "Never";
  try {
    const d = typeof dateStr === "string" ? new Date(dateStr) : dateStr;
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return String(dateStr);
  }
}

/**
 * Date without the time — "Mar 12, 2026". For captions that have to fit a
 * narrow column, where the hour adds nothing and forces an ellipsis.
 */
export function formatDate(dateStr: string | Date | null | undefined): string {
  if (!dateStr) return "Never";
  try {
    const d = typeof dateStr === "string" ? new Date(dateStr) : dateStr;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return String(dateStr);
  }
}

/** Compact form for tight table columns — "28 Jul, 12:07". Drops the year. */
export function formatCompactDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Formats a FHIR `date` (`YYYY-MM-DD`) for the meta line, tolerating partial dates. */
export function formatBirthDate(birthDate: string | null): string | null {
  if (!birthDate) return null;
  if (/^\d{4}$/.test(birthDate)) return birthDate;
  const yearMonth = /^(\d{4})-(\d{2})$/.exec(birthDate);
  if (yearMonth) {
    const year = Number(yearMonth[1]);
    const month = Number(yearMonth[2]);
    if (month < 1 || month > 12) return birthDate;
    return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(
      undefined,
      { month: "short", year: "numeric", timeZone: "UTC" },
    );
  }
  const fullDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthDate);
  if (!fullDate) return birthDate;
  const year = Number(fullDate[1]);
  const month = Number(fullDate[2]);
  const day = Number(fullDate[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year
    || parsed.getUTCMonth() !== month - 1
    || parsed.getUTCDate() !== day
  ) return birthDate;
  return parsed.toLocaleDateString(
    undefined,
    { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" },
  );
}

/** Formats an ISO date as e.g. "22 Jul 2026", or a dash when absent. */
export function formatDayMonthYear(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/** Whole days until `expiresAt`, floored at zero. */
export function daysLeft(expiresAt: Date | string): number {
  return Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86_400_000));
}

export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === "string" ? reader.result : null;
      const base64 = dataUrl?.split(",")[1];
      if (base64) resolve(base64);
      else reject(new Error("Failed to read file"));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

export function generateUniqueSlug(name: string): string {
  const baseSlug = slugify(name);
  const randomSuffix = Math.random().toString(36).substring(2, 6);
  return `${baseSlug}-${randomSuffix}`;
}
